<script setup lang="ts">
import PrimeButton from 'primevue/button';
import { computed, useSlots } from 'vue';
import Icon from './Icon.vue';

/**
 * Thin, additive wrapper around PrimeVue's `Button` (ADR-054 follow-up: Material Symbols
 * everywhere). Adds an optional `symbol` prop — a Material Symbols ligature name, rendered
 * through `Icon.vue` via Button's own `#icon` slot — alongside the legacy PrimeIcons `icon`
 * string prop that PrimeVue's Button already supports. `symbol` takes precedence when given;
 * every other prop/attr/listener (`label`, `severity`, `loading`, `size`, `text`, `rounded`,
 * `data-test`, `@click`, the legacy `icon`, ...) falls through to PrimeVue's Button unchanged,
 * so existing call sites that haven't migrated yet keep working.
 *
 * `symbolClass` is an optional extra class merged onto the rendered `Icon` (e.g.
 * `"rtl:rotate-180"` for a directional chevron that must mirror in RTL, matching the pattern
 * already used for plain `<Icon>` usages in AppShell/LoginPage).
 */
defineOptions({ name: 'JvButton', inheritAttrs: false });
const props = defineProps<{ symbol?: string; symbolClass?: string }>();
const slots = useSlots();
const forwardedSlots = computed(() => Object.keys(slots).filter((name) => name !== 'icon' || !props.symbol));
</script>

<template>
  <PrimeButton v-bind="$attrs">
    <template v-for="name in forwardedSlots" :key="name" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>
    <template v-if="symbol" #icon="{ class: iconClass }">
      <Icon :name="symbol" :size="16" :class="[iconClass, symbolClass]" />
    </template>
  </PrimeButton>
</template>
